/**
 * The prop bag `GlobalSearchBody` wants, for surfaces that PICK a player
 * (SE-7242 quick capture, SE-7524 the New report picker).
 *
 * **This hook owns no search of its own.** It composes the house
 * `usePlayerSearch` + `useFilterState` + `useAllSortOptions`, so every picker
 * renders the same search rather than one of its own. It replaces
 * `useAddPlayerSearch`, which ran a second, private `searchPlayers` document
 * against the same backend.
 *
 * It lives in `@/hooks` rather than inside a feature because the second
 * consumer arrived: a copy in `scouting` would be the duplication that
 * original extraction existed to end.
 *
 * **What it adds, and why it has to.** `usePlayerSearchPagination` skips only
 * on `!isOpen || !hasAuth || hasFilters` — no empty-query skip and no minimum
 * term length — and queries `network-only`. That is fine for a desktop search
 * modal the user opened deliberately. It is not fine for a picker: the retired
 * `useAddPlayerSearch` floored at three characters because a `LIKE` pattern
 * shorter than that cannot use the backend's trigram index and degrades to a
 * full scan, and these surfaces fire from a phone on stadium wifi, once per
 * debounce. So the floor is preserved by gating the composed hook's own
 * `isOpen`: below the floor the queries never mount. That is backend load, not
 * client weight, which is why it survived the rewrite rather than being
 * dropped as drawer-era detail.
 *
 * Filters are treated as a qualifying term in their own right — a filtered
 * search with no text is a real query, and the trigram concern does not apply
 * to it.
 * @module hooks/usePlayerSelectSearch
 */

import { useCallback, useMemo, useState } from "react";

import usePlayerSearch from "@/components/organisms/shared/search-modal/hooks/usePlayerSearch";
import { useAuth } from "@/features/auth";
import { useAllSortOptions } from "@/hooks/useAllSortOptions";
import useFilterState from "@/hooks/useFilterState";

/**
 * Minimum characters before the search fires. Three, not two: a `LIKE`
 * pattern under three characters defeats the backend's trigram index.
 * Carried over verbatim from the retired `useAddPlayerSearch`.
 */
export const MIN_PLAYER_SELECT_SEARCH_CHARS = 3;

/**
 * Drives a player-picking search surface.
 * @param isActive - Whether the hosting surface is showing the search. A
 * collapsed drawer, a closed sheet or an unopened modal must hold no
 * subscription — quick capture mounts one of these per squad column.
 * @returns The search state, the filter-panel state, and a `reset` for close.
 */
const usePlayerSelectSearch = (isActive: boolean) => {
  const auth = useAuth();
  const hasAuth = Boolean(auth?.data?.accessToken);

  /* Local, not `globalSearchShowFilterVar`: that var belongs to the sidebar
   * singleton, and writing it from here would leave the sidebar's filter panel
   * open the next time the user opened it. */
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const filterState = useFilterState();
  const allSortOptions = useAllSortOptions();

  /* The floor. `searchQuery` is the raw field, so this is evaluated against
   * what the scout has actually typed rather than against a settled term —
   * the point is to stop the query mounting at all, not to filter its result. */
  const [pendingQuery, setPendingQuery] = useState("");
  const qualifies =
    pendingQuery.trim().length >= MIN_PLAYER_SELECT_SEARCH_CHARS ||
    filterState.activeFilterCount > 0;

  const playerSearch = usePlayerSearch({
    isOpen: isActive && qualifies,
    hasAuth,
    sortOptions: allSortOptions,
    filterState,
  });

  const { handleSearchChange, handleClearSearch, resetSearchAndSort } =
    playerSearch;

  const onSearchChange = useCallback(
    (text: string) => {
      setPendingQuery(text);
      handleSearchChange(text);
    },
    [handleSearchChange]
  );

  const onClearSearch = useCallback(() => {
    setPendingQuery("");
    handleClearSearch();
  }, [handleClearSearch]);

  const onToggleFilterPanel = useCallback(
    () => setIsFilterPanelOpen(open => !open),
    []
  );
  const onCloseFilterPanel = useCallback(() => setIsFilterPanelOpen(false), []);

  /** Returns the surface to its opening state — called when it closes. */
  const reset = useCallback(() => {
    setPendingQuery("");
    resetSearchAndSort();
    filterState.closeFilterSheet();
    setIsFilterPanelOpen(false);
  }, [filterState, resetSearchAndSort]);

  return useMemo(
    () => ({
      searchQuery: playerSearch.searchQuery,
      onSearchChange,
      onClearSearch,
      activeFilterCount: filterState.activeFilterCount,
      onToggleFilterPanel,
      onCloseFilterPanel,
      isFilterPanelOpen,
      filterState,
      positionOptions: filterState.positionOptions,
      clubOptions: filterState.clubOptions,
      effectiveSortBy: playerSearch.effectiveSortBy,
      sortFieldLabel: playerSearch.sortFieldLabel,
      onToggleSort: playerSearch.handleToggleSort,
      sortDropdownProps: playerSearch.sortDropdownProps,
      players: playerSearch.searchPlayers,
      loading: playerSearch.isLoading,
      isRefreshing: playerSearch.isRefreshing,
      hasMore: playerSearch.hasMore,
      loadMore: playerSearch.loadMore,
      isLoadingMore: playerSearch.isLoadingMore,
      isEmpty: playerSearch.isEmpty,
      isError: playerSearch.isError,
      activeFilterChips: playerSearch.activeFilterChips,
      chipRemoveHandlers: playerSearch.chipRemoveHandlers,
      // Below the floor there is nothing to show and nothing to apologise for:
      // `hasSearched` false renders an empty pane rather than shouting "no
      // players found" at somebody who is mid-word. Same rule the retired
      // drawer applied to its own "no match" state.
      hasSearched: playerSearch.hasSearched,
      reset,
    }),
    [
      filterState,
      isFilterPanelOpen,
      onClearSearch,
      onCloseFilterPanel,
      onSearchChange,
      onToggleFilterPanel,
      playerSearch,
      reset,
    ]
  );
};

export default usePlayerSelectSearch;
